import React, { useEffect, useState } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { Zap, Plus, Trash2, Edit, FileText, Droplet, Wifi, Shield, Users, Home, Loader2, X } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/contexts/ToastContext';

const UTILITY_CARDS = [
    { id: 'water', name: 'Water', icon: Droplet, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    { id: 'electricity', name: 'Electricity', icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
    { id: 'garbage', name: 'Garbage Collection', icon: Trash2, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30' },
    { id: 'internet', name: 'Internet/Wi-Fi', icon: Wifi, color: 'text-indigo-500', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
    { id: 'security', name: 'Security', icon: Shield, color: 'text-danger dark:text-danger-dark', bg: 'bg-red-100 dark:bg-red-900/30' },
    { id: 'other', name: 'Other Utility', icon: Plus, color: 'text-light-subtle dark:text-dark-subtle', bg: 'bg-gray-100 dark:bg-gray-800' },
];

export const UtilitiesManager: React.FC = () => {
    const { properties, fetchProperties, utilities, fetchUtilities, units, fetchUnits, fetchTenants, fetchInvoices, billUtility } = useLandlordStore();
    const { success, error: toastError } = useToast();
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
    
    // Billing Form State
    const [isBillingFormOpen, setIsBillingFormOpen] = useState(false);
    const [utilityType, setUtilityType] = useState<string>('Water');
    const [target, setTarget] = useState<'All' | 'Specific'>('All');
    const [unitId, setUnitId] = useState('');
    const [amount, setAmount] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchProperties();
    }, []);

    useEffect(() => {
        if (properties.length > 0 && !selectedPropertyId) {
            setSelectedPropertyId(properties[0].id);
        }
    }, [properties]);

    useEffect(() => {
        if (selectedPropertyId) {
            fetchUtilities(selectedPropertyId);
            fetchUnits(selectedPropertyId);
            fetchTenants();
            fetchInvoices();
        }
    }, [selectedPropertyId]);

    const handleCardClick = (utilityName: string) => {
        setUtilityType(utilityName);
        setIsBillingFormOpen(true);
        // Scroll to bottom smoothly where the form is appended
        setTimeout(() => {
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }, 100);
    };

    const propertyUnits = units.filter(u => u.property_id === selectedPropertyId);

    const handleBillSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
            setError("Please enter a valid amount.");
            return;
        }

        if (!dueDate) {
            setError("Please select a due date.");
            return;
        }

        if (target === 'Specific' && !unitId) {
            setError("Please select a specific unit.");
            return;
        }

        setIsSubmitting(true);
        try {
            await billUtility(selectedPropertyId, utilityType, target, unitId, Number(amount), dueDate);
            success('Bill generated', `Successfully added ${utilityType} bill!`);
            setIsBillingFormOpen(false);
            setAmount('');
            setUnitId('');
        } catch (err: any) {
            setError(err.message || "Failed to generate utility bills. Ensure there are active tenants.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!selectedPropertyId && properties.length === 0) return <LoadingScreen />;

    return (
        <div className="space-y-6 max-w-6xl mx-auto pb-24">
            <div className="flex justify-between items-center bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Utilities Management</h1>
            </div>

            {/* Property Selector */}
            <div className="bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border flex flex-col sm:flex-row sm:items-center gap-4">
                <label className="text-sm font-semibold text-light-subtle dark:text-dark-subtle whitespace-nowrap">Active Property:</label>
                <select
                    value={selectedPropertyId}
                    onChange={(e) => {
                        setSelectedPropertyId(e.target.value);
                        setIsBillingFormOpen(false); // close form on property switch
                    }}
                    className="w-full sm:w-80 px-4 py-2 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-light-text dark:text-dark-text"
                >
                    {properties.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {/* Utility Cards */}
            <div>
                <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Generate Bill for Utility</h2>
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                    {UTILITY_CARDS.map(card => (
                        <button
                            key={card.id}
                            onClick={() => handleCardClick(card.name)}
                            className="flex flex-col items-center justify-center p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl hover:shadow-md transition-all hover:-translate-y-1 hover:border-primary/50"
                        >
                            <div className={`p-4 rounded-full ${card.bg} mb-3`}>
                                <card.icon className={card.color} size={28} />
                            </div>
                            <span className="text-sm font-bold text-light-text dark:text-dark-text text-center">
                                {card.name}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Existing Utilities Table */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-light-bg dark:bg-dark-bg">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Utility Name</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Type</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Cost</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider hidden md:table-cell">Unit</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-light-border dark:divide-dark-border">
                        {utilities.length > 0 ? (
                            utilities.map((utility) => (
                                <tr key={utility.id} className="hover:bg-light-bg dark:bg-dark-bg transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
                                                <Zap size={18} />
                                            </div>
                                            <span className="font-bold text-light-text dark:text-dark-text">{utility.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">{utility.type}</td>
                                    <td className="px-6 py-4 text-sm font-bold text-light-text dark:text-dark-text">
                                        KES {utility.cost.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium text-light-subtle dark:text-dark-subtle hidden md:table-cell">{utility.unit}</td>
                                    <td className="px-6 py-4 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button className="p-2 text-light-subtle dark:text-dark-subtle hover:text-primary transition-colors hover:bg-primary/10 rounded-full">
                                                <Edit size={16} />
                                            </button>
                                            <button className="p-2 text-light-subtle dark:text-dark-subtle hover:text-danger dark:hover:text-red-400 transition-colors hover:bg-danger/10 rounded-full">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="px-6 py-12 text-center text-light-subtle dark:text-dark-subtle font-medium">
                                    No utilities configured for this property. Click a category above to create one.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Inline Billing Form Rendering */}
            <AnimatePresence>
                {isBillingFormOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-light-card dark:bg-dark-card rounded-xl border-2 border-primary/20 shadow-lg overflow-hidden mt-8"
                    >
                        <div className="flex justify-between items-center p-6 border-b border-light-border dark:border-dark-border bg-primary/5">
                            <h2 className="text-xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                                <FileText className="text-primary" size={24} />
                                New <span className="text-primary">{utilityType}</span> Bill
                            </h2>
                            <button onClick={() => setIsBillingFormOpen(false)} className="p-2 rounded-full hover:bg-white dark:hover:bg-dark-bg text-light-subtle dark:text-dark-subtle transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleBillSubmit} className="p-6 space-y-6">
                            {error && (
                                <div className="p-4 bg-danger/10 text-danger dark:text-red-400 rounded-xl text-sm border border-danger/20 font-medium">
                                    {error}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-3">
                                            Billing Target
                                        </label>
                                        <div className="grid grid-cols-2 gap-4">
                                            <label className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${target === 'All' ? 'border-primary bg-primary/10' : 'border-light-border dark:border-dark-border hover:border-primary/50 bg-light-input dark:bg-dark-input'}`}>
                                                <input
                                                    type="radio"
                                                    name="target"
                                                    value="All"
                                                    checked={target === 'All'}
                                                    onChange={() => setTarget('All')}
                                                    className="sr-only"
                                                />
                                                <Users className={`mb-2 ${target === 'All' ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`} size={24} />
                                                <span className={`text-sm font-bold ${target === 'All' ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`}>All Units</span>
                                            </label>
                                            <label className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${target === 'Specific' ? 'border-primary bg-primary/10' : 'border-light-border dark:border-dark-border hover:border-primary/50 bg-light-input dark:bg-dark-input'}`}>
                                                <input
                                                    type="radio"
                                                    name="target"
                                                    value="Specific"
                                                    checked={target === 'Specific'}
                                                    onChange={() => setTarget('Specific')}
                                                    className="sr-only"
                                                />
                                                <Home className={`mb-2 ${target === 'Specific' ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`} size={24} />
                                                <span className={`text-sm font-bold ${target === 'Specific' ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`}>Specific Unit</span>
                                            </label>
                                        </div>
                                    </div>

                                    {target === 'Specific' && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                        >
                                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                                Select Unit
                                            </label>
                                            <select
                                                required={target === 'Specific'}
                                                value={unitId}
                                                onChange={(e) => setUnitId(e.target.value)}
                                                className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl focus:ring-2 focus:ring-primary/50 outline-none text-light-text dark:text-dark-text font-medium"
                                            >
                                                <option value="">-- Choose Unit --</option>
                                                {propertyUnits.map(unit => (
                                                    <option key={unit.id} value={unit.id}>Unit {unit.unit_number}</option>
                                                ))}
                                            </select>
                                        </motion.div>
                                    )}
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                            Amount (KES)
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-light-subtle dark:text-dark-subtle">KES</span>
                                            <input
                                                type="number"
                                                required
                                                min="0"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                placeholder="0.00"
                                                className="w-full pl-14 pr-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl focus:ring-2 focus:ring-primary/50 outline-none text-light-text dark:text-dark-text font-bold text-lg"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                            Due Date
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={dueDate}
                                            onChange={(e) => setDueDate(e.target.value)}
                                            className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl focus:ring-2 focus:ring-primary/50 outline-none text-light-text dark:text-dark-text font-medium"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-6 border-t border-light-border dark:border-dark-border mt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsBillingFormOpen(false)}
                                    className="px-6 py-3 font-bold text-light-subtle dark:text-dark-subtle bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl hover:bg-light-border dark:hover:bg-dark-border transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-8 py-3 font-bold text-white bg-primary rounded-xl hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2 shadow-sm transition-all"
                                >
                                    {isSubmitting ? (
                                        <><Loader2 className="animate-spin" size={20} /> Generating</>
                                    ) : (
                                        <>Generate Bill</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
